import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Gift, Eye, EyeOff } from 'lucide-react';
import { ToastNotification } from '../dashboard/ResumeDiagnosis/styles/modals';

const UnifiedLoginModal = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState('login'); // 'login' 或 'register'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    code: '',
    captcha: '',
    inviteCode: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [agreedToTerms, setAgreedToTerms] = useState(false); // 默认不选中
  const [inviteCodeValid, setInviteCodeValid] = useState(null);
  const [inviteCodeInfo, setInviteCodeInfo] = useState(null);
  const [showInviteCodeField, setShowInviteCodeField] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [showToast, setShowToast] = useState(false);

  const { login, register, sendVerificationCode, sendForgetPassword, validateInviteCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 从后端获取图形验证码
  const fetchCaptcha = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'development' ? '' : '');
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/captcha`);
      const data = await response.json();

      if (data.success) {
        setCaptchaId(data.captcha_id);
        setCaptchaImage(data.image);
        // 如果后端返回了code（开发环境），也保存下来用于本地验证
        if (data.code) {
          setCaptchaCode(data.code);
        }
      } else {
        console.error('获取验证码失败');
        setError('获取验证码失败，请刷新页面重试');
      }
    } catch (error) {
      console.error('获取验证码失败:', error);
      setError('获取验证码失败，请检查网络连接');
    }
  };

  // 验证邀请码
  const handleValidateInviteCode = async (code) => {
    if (!code.trim()) {
      setInviteCodeValid(null);
      setInviteCodeInfo(null);
      return;
    }

    try {
      const result = await validateInviteCode(code.trim());
      if (result.valid) {
        setInviteCodeValid(true);
        setInviteCodeInfo({
          // inviterName: result.inviterName || "职唛惠分享用户",
          // inviterId: result.inviterId
        });
        setError('');
      } else {
        setInviteCodeValid(false);
        setInviteCodeInfo(null);
        setError(result.message || '邀请码无效');
      }
    } catch (err) {
      setInviteCodeValid(false);
      setInviteCodeInfo(null);
      setError(err.message);
    }
  };

  // 从URL参数获取邀请码
  useEffect(() => {
    if (isOpen) {
      // 重置所有表单数据
      setFormData({ email: '', password: '', code: '', captcha: '', inviteCode: '' });
      setError('');
      setSuccessMessage('');
      setCodeSent(false);
      setCountdown(0);
      setAgreedToTerms(false);
      setInviteCodeValid(null);
      setInviteCodeInfo(null);
      setShowInviteCodeField(false);

      const urlParams = new URLSearchParams(location.search);
      const inviteCodeFromUrl = urlParams.get('inviteCode');

      if (inviteCodeFromUrl) {
        // 如果有邀请码，默认显示注册页面
        setMode('register');
        setFormData(prev => ({ ...prev, inviteCode: inviteCodeFromUrl }));
        setShowInviteCodeField(true);
        handleValidateInviteCode(inviteCodeFromUrl);
        // 注册模式需要验证码
        fetchCaptcha();
      } else {
        // 没有邀请码时，默认显示登录页面
        setMode('login');
        // 登录模式不需要验证码
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, location.search]);

  // 当切换到需要验证码的模式时，获取验证码
  useEffect(() => {
    if (isOpen && (mode === 'register' || mode === 'resetPassword')) {
      // 只有在没有验证码或验证码过期时才重新获取
      if (!captchaId || !captchaImage) {
        fetchCaptcha();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, isOpen]);

  // 验证码倒计时
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSendCode = async () => {
    if (!formData.email) {
      setError('请输入邮箱地址');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('请输入正确的邮箱地址');
      return;
    }

    // 只在注册模式下检查图形验证码
    if (mode === 'register') {
      if (!formData.captcha) {
        setError('请输入图形验证码');
        return;
      }

      // 注意：这里不再需要本地验证，将由后端验证
    }

    setIsSendingCode(true);
    setError('');

    try {
      // 在注册模式下传递图形验证码
      const captchaIdToSend = mode === 'register' ? captchaId : null;
      const captchaCodeToSend = mode === 'register' ? formData.captcha : null;
      await sendVerificationCode(formData.email, "email", captchaIdToSend, captchaCodeToSend);
      setCodeSent(true);
      setCountdown(60);
    } catch (err) {
      setError(err.message);
      // 验证码错误，刷新验证码
      if (mode === 'register') {
        fetchCaptcha();
        setFormData(prev => ({ ...prev, captcha: '' }));
      }
    } finally {
      setIsSendingCode(false);
    }
  };


  const handleForgetPassword = async () => {
    if (!formData.email) {
      setError('请输入邮箱地址');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('请输入正确的邮箱地址');
      return;
    }

    // 在忘记密码模式下检查图形验证码
    if (!formData.captcha) {
      setError('请输入图形验证码');
      return;
    }

    if (!captchaId) {
      setError('验证码已过期，请刷新后重试');
      fetchCaptcha();
      return;
    }

    setIsSendingCode(true);
    setError('');
    setSuccessMessage('');

    try {
      await sendForgetPassword(formData.email, "email", captchaId, formData.captcha);
      setToastMessage('重置请求已发送');
      setShowToast(true);
      setCodeSent(true);
      setCountdown(60);
      setTimeout(() => {
        setShowToast(false);
        setTimeout(() => setToastMessage(null), 300);
      }, 2000);
    } catch (err) {
      setError(err.message || '发送失败，请稍后重试');
      // 验证码错误，刷新验证码
      fetchCaptcha();
      setFormData(prev => ({ ...prev, captcha: '' }));
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!formData.email) {
        throw new Error('请输入邮箱地址');
      }

      // 只在注册模式下检查协议同意
      if (mode === 'register' && !agreedToTerms) {
        throw new Error('请先同意用户服务协议和隐私协议');
      }

      // 如果有邀请码，先验证（只在注册模式下）
      if (mode === 'register' && formData.inviteCode && !inviteCodeValid) {
        throw new Error('请先验证邀请码');
      }

      if (mode === 'login') {
        // 登录模式
        if (!formData.password) {
          throw new Error('请输入密码');
        }

        await login(formData.email, formData.password, 'password');
      } else if (mode === 'register') {
        // 注册模式
        if (!formData.password) {
          throw new Error('请输入密码');
        }

        if (formData.password.length < 8) {
          throw new Error('密码至少需要8位');
        }

        if (formData.password.length > 16) {
          throw new Error('密码不能超过16位');
        }

        if (!formData.code) {
          throw new Error('请输入邮箱验证码');
        }

        await register({
          email: formData.email,
          password: formData.password,
          code: formData.code,
          inviteCode: formData.inviteCode,
          inviterId: inviteCodeInfo?.inviterId,
          inviterName: inviteCodeInfo?.inviterName,
          loginType: 'email'
        });
      } else if (mode === 'resetPassword') {
        // 重置密码模式
        handleForgetPassword();
      }

      // 登录/注册成功后关闭模态框并跳转到仪表盘
      // 忘记密码模式下不关闭弹窗
      if (mode !== 'resetPassword') {
        onClose();
        navigate('/app');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // 如果是邀请码字段，实时验证（只在注册模式下）
    if (name === 'inviteCode' && mode === 'register') {
      if (value.trim()) {
        handleValidateInviteCode(value);
      } else {
        setInviteCodeValid(null);
        setInviteCodeInfo(null);
      }
    }
  };

  const handleClose = () => {
    if (!isLoading && !isSendingCode) {
      setFormData({ email: '', password: '', code: '', captcha: '', inviteCode: '' });
      setError('');
      setCodeSent(false);
      setCountdown(0);
      setAgreedToTerms(false); // 重置为默认不选中
      setInviteCodeValid(null);
      setInviteCodeInfo(null);
      setShowInviteCodeField(false);
      setMode('login'); // 重置为登录模式
      // 清空验证码相关状态
      setCaptchaId('');
      setCaptchaImage('');
      setCaptchaCode('');
      onClose();
    }
  };



  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose} disabled={isLoading || isSendingCode}>
          <X size={20} />
        </CloseButton>

        <Logo>
          <BrandContainer>
            <BrandIcon src="/images/logos/logo.svg" alt="职唛 AI Logo" />
            <BrandText>职唛 AI</BrandText>
          </BrandContainer>
        </Logo>



        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <InputWrapper>
              <Input
                type="email"
                name="email"
                placeholder="请输入邮箱地址"
                value={formData.email}
                onChange={handleChange}
                disabled={mode === 'resetPassword' && codeSent}
                required
              />
            </InputWrapper>
          </InputGroup>

          {/* 密码输入 */}

          {mode !== 'resetPassword' && (
            <>
              <InputGroup>
                <InputWrapper>
                  <Input
                    type={isPasswordVisible ? "text" : "password"}
                    name="password"
                    placeholder={mode === 'register' ? "8-16位，含字母/数字/特殊字符" : "请输入密码"}
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  {formData.password && (
                    <PasswordIconButton
                      onMouseEnter={() => setIsPasswordVisible(true)}
                      onMouseLeave={() => setIsPasswordVisible(false)}
                      type="button"
                      tabIndex={-1}
                    >
                      {isPasswordVisible ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </PasswordIconButton>
                  )}
                </InputWrapper>
              </InputGroup>
            </>
          )}

          {/* 忘记密码链接 - 只在登录模式显示 */}
          {mode === 'login' && (
            <ForgotPasswordLink>
              {/* 注意：必须显式指定 type="button"，否则在表单中默认为 submit，
                    会导致在输入框按回车时优先触发这个按钮，从而切换到重置密码模式 */}
              <ForgotPasswordText
                type="button"
                onClick={() => setMode('resetPassword')}
              >
                忘记密码？
              </ForgotPasswordText>
            </ForgotPasswordLink>
          )}

          {/* 注册模式和忘记密码模式都显示图形验证码 */}
          {(mode === 'register' || mode === 'resetPassword') && (
            <>
              {/* 图形验证码 */}
              <InputGroup>
                <CaptchaWrapper>
                  <Input
                    type="text"
                    name="captcha"
                    placeholder="请输入图形验证码"
                    value={formData.captcha}
                    onChange={handleChange}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    maxLength={4}
                    disabled={mode === 'resetPassword' && codeSent}
                    required
                  />
                  <CaptchaDisplay onClick={() => {
                    if (mode === 'resetPassword' && codeSent) return;
                    fetchCaptcha();
                    setFormData(prev => ({ ...prev, captcha: '' }));
                  }}>
                    {captchaImage ? (
                      <CaptchaImg src={captchaImage} alt="验证码" />
                    ) : (
                      <CaptchaText>加载中...</CaptchaText>
                    )}
                  </CaptchaDisplay>
                </CaptchaWrapper>
              </InputGroup>

              {/* 只有注册模式才显示邮箱验证码 */}
              {mode === 'register' && (
                <InputGroup>
                  <InputWrapper>
                    <Input
                      type="text"
                      name="code"
                      placeholder="请输入邮箱验证码"
                      value={formData.code}
                      onChange={handleChange}
                      autoComplete="off"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      required
                    />
                    <SendCodeButton
                      type="button"
                      onClick={handleSendCode}
                      disabled={countdown > 0 || isSendingCode}
                    >
                      {countdown > 0 ? `${countdown}s` : isSendingCode ? '发送中...' : '获取验证码'}
                    </SendCodeButton>
                  </InputWrapper>
                </InputGroup>
              )}
            </>
          )}

          {/* 邀请码输入区域 - 只在注册模式且有URL邀请码参数时显示 */}
          {mode === 'register' && showInviteCodeField && (
            <InviteCodeSection>
              <InviteCodeToggle onClick={() => setShowInviteCodeField(false)}>
                <Gift size={16} />
                隐藏邀请码
              </InviteCodeToggle>

              <InputGroup>
                <InputWrapper>
                  <Input
                    type="text"
                    name="inviteCode"
                    placeholder="请输入邀请码（可选）"
                    value={formData.inviteCode}
                    onChange={handleChange}
                    className={inviteCodeValid ? 'valid' : ''}
                  />
                  {inviteCodeValid && inviteCodeInfo && (
                    <InviteCodeInfo>
                      <InviteCodeIcon>✓</InviteCodeIcon>
                      {/* <InviteCodeText>来自 {inviteCodeInfo.inviterName}</InviteCodeText> */}
                    </InviteCodeInfo>
                  )}
                </InputWrapper>
              </InputGroup>
            </InviteCodeSection>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {mode !== 'resetPassword' && successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}

          <LoginButton
            type="submit"
            disabled={
              isLoading ||
              (mode === 'register' && !codeSent) ||
              (mode === 'register' && !agreedToTerms) ||
              (mode === 'resetPassword' && (isSendingCode || codeSent))
            }
          >
            {isLoading ? (mode === 'login' ? '登录中...' : '注册中...') : (mode === 'resetPassword' ? '发送邮件' : (mode === 'login' ? '登录' : '注册'))}
          </LoginButton>
        </Form>

        {/* 切换按钮 */}
        <ModeSwitch>
          {mode === 'login' ? (
            <>
              <ModeText>还没有账户？</ModeText>
              <ModeLink onClick={() => setMode('register')}>点击注册</ModeLink>
            </>
          ) : (
            <>
              <ModeText>已有账户？</ModeText>
              <ModeLink onClick={() => setMode('login')}>点击登录</ModeLink>
            </>
          )}
        </ModeSwitch>

        {mode === 'register' && (
          <Agreement>
            <AgreementCheckbox>
              <CheckboxWrapper>
                <CheckboxInput
                  type="checkbox"
                  id="agreement"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                />
                <CheckboxLabel htmlFor="agreement">
                  <CheckboxIcon checked={agreedToTerms}>
                    {agreedToTerms && '✓'}
                  </CheckboxIcon>
                </CheckboxLabel>
              </CheckboxWrapper>
              <AgreementText>
                我已同意并遵守《
                <AgreementLink href="/privacy">隐私协议</AgreementLink>
                》和《
                <AgreementLink href="/terms">用户服务协议</AgreementLink>
                》
              </AgreementText>
            </AgreementCheckbox>
          </Agreement>
        )}

        {toastMessage && (
          <ToastNotification show={showToast}>
            {toastMessage}
          </ToastNotification>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

// 样式组件
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 360px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  position: relative;
  padding: 32px;
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: #374151;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const BrandContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 1.5rem;
  color: #1a1a1a;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const BrandIcon = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 8px;
  transition: all 0.2s ease;
`;

const BrandText = styled.span`
  color: #2c3e50;
`;



const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  position: relative;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;


const Input = styled.input`
  width: 100%;
  padding: 16px 20px 16px ${props => props.name === 'phone' ? '60px' : '20px'};
  /* Ensure code input text doesn't overlap the countdown button */
  padding-right: ${props => props.name === 'code' ? '110px' : '20px'};
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: #f8f9fa;
  /* Hide Safari/Chrome in-field autofill accessory icons */
  &::-webkit-contacts-auto-fill-button,
  &::-webkit-credentials-auto-fill-button,
  &::-webkit-calendar-picker-indicator,
  &::-ms-reveal,
  &::-ms-clear {
    visibility: hidden;
    pointer-events: none;
    position: absolute;
    right: 0;
    display: none;
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  &:focus {
    outline: none;
    border-color: #667eea;
    background: white;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #adb5bd;
    font-size: 14px;
  }

  &.valid {
    border-color: #10b981;
    background: #f0fdf4;
  }

  &.invalid {
    border-color: #ef4444;
    background: #fef2f2;
  }
`;

const SendCodeButton = styled.button`
  position: absolute;
  right: 12px;
  min-width: 96px;
  text-align: center;
  background: none;
  border: none;
  color: #667eea;
  cursor: pointer;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: rgba(102, 126, 234, 0.1);
  }
  
  &:disabled {
    color: #adb5bd;
    cursor: not-allowed;
  }
`;

const InviteCodeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InviteCodeToggle = styled.button`
  background: none;
  border: none;
  color: #667eea;
  cursor: pointer;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: rgba(102, 126, 234, 0.1);
  }
`;

const InviteCodeInfo = styled.div`
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: #10b981;
  font-size: 12px;
  font-weight: 500;
`;

const InviteCodeIcon = styled.span`
  color: #10b981;
  font-weight: bold;
`;

const InviteCodeText = styled.span`
  color: #10b981;
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 14px;
  text-align: center;
  padding: 12px;
  background: #fdf2f2;
  border-radius: 8px;
  border: 1px solid #fecaca;
`;

const SuccessMessage = styled.div`
  color: #10b981;
  font-size: 14px;
  text-align: center;
  padding: 12px;
  background: #f0fdf4;
  border-radius: 8px;
  border: 1px solid #86efac;
`;


const Agreement = styled.div`
  margin-top: 16px;
  text-align: left;
`;

const AgreementCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  justify-content: flex-start;
`;

const CheckboxWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const CheckboxInput = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 2px solid #e9ecef;
  border-radius: 3px;
  background-color: white;
  transition: all 0.2s ease;
  position: relative;
  flex-shrink: 0;

  &:hover {
    border-color: #667eea;
  }

  &:focus-within {
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  }
`;

const CheckboxIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background-color: ${props => props.checked ? '#667eea' : 'transparent'};
  border: none;
  transition: all 0.2s ease;
  color: white;
  font-size: 8px;
  font-weight: bold;
  line-height: 1;
`;

const AgreementText = styled.p`
  font-size: 12px;
  color: #8b949e;
  line-height: 1.4;
  margin: 0;
  flex: 1;
  white-space: nowrap;
  text-align: left;
`;

const AgreementLink = styled.a`
  color: #667eea;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CaptchaWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const CaptchaDisplay = styled.div`
  width: 120px;
  height: 50px;
  background: #f5f7fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  overflow: hidden;
  position: relative;
  
  &:hover {
    border-color: #667eea;
    transform: scale(1.02);
  }
  
  &:hover::after {
    content: '点击刷新';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(102, 126, 234, 0.9);
    color: white;
    font-size: 10px;
    padding: 2px 0;
    text-align: center;
  }
`;

const CaptchaImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CaptchaText = styled.span`
  font-size: 16px;
  font-weight: bold;
  color: #333;
  letter-spacing: 2px;
  text-decoration: line-through;
  text-decoration-color: #999;
`;

// 新增的样式组件
const ModeSwitch = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const ModeText = styled.span`
  color: #64748b;
  font-size: 14px;
`;

const ModeLink = styled.button`
  background: none;
  border: none;
  color: #667eea;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: #5a67d8;
    text-decoration: underline;
  }
`;

const ForgotPasswordLink = styled.div`
  text-align: right;
  margin-top: -10px;
  margin-bottom: 10px;
`;

const ForgotPasswordText = styled.button`
  background: none;
  border: none;
  color: #667eea;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #5a67d8;
    text-decoration: underline;
  }
`;

const PasswordIconButton = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  
  ${InputWrapper}:hover & {
    opacity: 1;
    pointer-events: auto;
  }
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
  
  &:focus {
    outline: none;
  }
`;

export default UnifiedLoginModal;
